import React, { useCallback, useState, useMemo } from "react";
import * as Sentry from "@sentry/react";
import moment from "moment";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiButtonGroup,
  EuiFieldText,
  EuiForm,
  EuiFormRow,
  EuiIcon,
  EuiImage,
  EuiMarkdownEditor,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiText,
  EuiToolTip,
  EuiLoadingSpinner,
  EuiCallOut,
  EuiMarkdownParseError,
  EuiFilePicker,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiButtonIcon,
  EuiProgress,
} from "@elastic/eui";
import type { VFileMessage } from "vfile-message";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";
import { AutonubilSvg } from "src/assets";
import { AnketaTranslationOptions } from "src/internationalization/types/context";
import { useAnketaAuth } from "src/auth/context";

const development = false;

interface FeedbackFormData {
  name: string;
  email: string;
  feedback: string;
  severity: Sentry.SeverityLevel;
}

interface FeedbackToggleButton {
  id: Sentry.SeverityLevel;
  label: string;
  description: string;
}

interface ImageAttachment {
  id: string;
  file: File;
  preview: string;
  base64: string;
  uploading?: boolean;
  error?: string;
}

interface FeedbackReply {
  id: string;
  message: string;
  timestamp: number;
  author: string;
  email: string;
}

interface StoredFeedback {
  eventId: string;
  name: string;
  email: string;
  message: string;
  severity: Sentry.SeverityLevel;
  timestamp: number;
  replies: FeedbackReply[];
  status: "open" | "resolved" | "in-progress";
  attachments?: {
    count: number;
    files: Array<{
      name: string;
      size: number;
      type: string;
    }>;
  };
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];
const MAX_IMAGES = 5;
const FEEDBACK_STORAGE_KEY = "opsanio_user_feedback";

const validateForm = (
  formData: FeedbackFormData,
  translate: (key: string, options?: AnketaTranslationOptions) => string
): string[] => {
  const errors: string[] = [];

  if (!formData.name.trim()) {
    errors.push(
      translate("anketa.feedback.form.name.required", {
        defaultMessage: "Name is required",
      })
    );
  }

  if (!formData.email.trim()) {
    errors.push(
      translate("anketa.feedback.form.email.required", {
        defaultMessage: "Email is required",
      })
    );
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.push(
      translate("anketa.feedback.form.email.invalid", {
        defaultMessage: "Please enter a valid email address",
      })
    );
  }

  if (!formData.feedback.trim()) {
    errors.push(
      translate("anketa.feedback.form.content.required", {
        defaultMessage: "Feedback is required",
      })
    );
  }

  return errors;
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

const validateImageFile = (
  file: File,
  translate: (key: string, options?: AnketaTranslationOptions) => string
): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return translate("anketa.feedback.images.error.type", {
      defaultMessage: "Only JPEG, PNG, GIF, and WebP images are allowed",
    });
  }

  if (file.size > MAX_FILE_SIZE) {
    return translate("anketa.feedback.images.error.size", {
      defaultMessage: "Image size must be less than 5MB",
    });
  }

  return null;
};

export const ButtonUserFeedback = React.memo(({}: {}): React.ReactElement => {
  const { translate } = useAnketaI18n();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleToggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div>
      <EuiToolTip
        content={
          <EuiText size="s">
            <p>
              {translate("anketa.feedback.button.tooltip", {
                defaultMessage:
                  "Share your feedback, report bugs, or suggest improvements",
              })}
            </p>
          </EuiText>
        }
      >
        <EuiButtonEmpty
          style={{ width: "50px" }}
          onClick={handleToggleModal}
          aria-label={translate("anketa.feedback.button.aria", {
            defaultMessage: "Open feedback modal",
          })}
        >
          <EuiIcon type="reporter" />
        </EuiButtonEmpty>
      </EuiToolTip>

      {isModalOpen && <OpsUserFeedbackModal onClose={handleCloseModal} />}
    </div>
  );
});

ButtonUserFeedback.displayName = "ButtonUf";

export const OpsUserFeedbackModal = React.memo(
  ({ onClose }: { onClose: () => void }): React.ReactElement => {
    const { translate } = useAnketaI18n();
    const { addToast } = useToast();
    const { user } = useAnketaAuth();

    const INITIAL_FORM_STATE: FeedbackFormData = {
      name:
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.username,
      email: user.email,
      feedback: "",
      severity: "info",
    };

    const [formData, setFormData] =
      useState<FeedbackFormData>(INITIAL_FORM_STATE);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [markdownErrors, setMarkdownErrors] = useState<VFileMessage[]>([]);
    const [imageAttachments, setImageAttachments] = useState<ImageAttachment[]>(
      []
    );
    const [uploadProgress, setUploadProgress] = useState<number>(0);

    const validationErrors = useMemo(
      () => validateForm(formData, translate),
      [formData]
    );
    const isFormValid = validationErrors.length === 0;

    const feedbackTypes: FeedbackToggleButton[] = useMemo(
      () => [
        {
          id: "info",
          label: translate("anketa.feedback.form.type.general", {
            defaultMessage: "General",
          }),
          description: translate(
            "anketa.feedback.form.type.general.description",
            {
              defaultMessage: "General feedback or questions",
            }
          ),
        },
        {
          id: "error",
          label: translate("anketa.feedback.form.type.bug", {
            defaultMessage: "Bug Report",
          }),
          description: translate("anketa.feedback.form.type.bug.description", {
            defaultMessage: "Something is broken or not working",
          }),
        },
        {
          id: "warning",
          label: translate("anketa.feedback.form.type.feature", {
            defaultMessage: "Feature Request",
          }),
          description: translate(
            "anketa.feedback.form.type.feature.description",
            {
              defaultMessage: "Ideas for improvements or new features",
            }
          ),
        },
      ],
      []
    );

    const handleFieldChange = useCallback(
      (field: keyof FeedbackFormData) => (value: string) => {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
        if (submitError) {
          setSubmitError(null);
        }
      },
      [submitError]
    );

    const handleSeverityChange = useCallback((severity: string) => {
      setFormData((prev) => ({
        ...prev,
        severity: severity as Sentry.SeverityLevel,
      }));
    }, []);

    const handleMarkdownParse = useCallback(
      (
        error: EuiMarkdownParseError | null,
        { messages }: { messages: VFileMessage[] }
      ) => {
        if (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          setMarkdownErrors([{ message: errorMessage } as VFileMessage]);
        } else {
          setMarkdownErrors(messages);
        }
      },
      []
    );

    const handleImageUpload = useCallback(
      async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const newImages: ImageAttachment[] = [];
        const currentImageCount = imageAttachments.length;

        if (currentImageCount + files.length > MAX_IMAGES) {
          setSubmitError(
            translate("anketa.feedback.images.error.maxCount", {
              defaultMessage: "You can upload a maximum of {maxImages} images",
              values: { maxImages: MAX_IMAGES },
            })
          );
          return;
        }

        setUploadProgress(0);

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const validationError = validateImageFile(file, translate);

          if (validationError) {
            setSubmitError(validationError);
            continue;
          }

          try {
            setUploadProgress((i / files.length) * 100);

            const id = `${Date.now()}-${i}`;
            const preview = URL.createObjectURL(file);
            const base64 = await fileToBase64(file);

            newImages.push({
              id,
              file,
              preview,
              base64,
            });
          } catch (error) {
            console.error("Error processing image:", error);
            setSubmitError(
              translate("anketa.feedback.images.error.processing", {
                defaultMessage: "Error processing image: {fileName}",
                values: { fileName: file.name },
              })
            );
          }
        }

        setUploadProgress(100);
        setImageAttachments((prev) => [...prev, ...newImages]);

        setTimeout(() => setUploadProgress(0), 1000);
      },
      [imageAttachments.length]
    );

    const handleRemoveImage = useCallback((imageId: string) => {
      setImageAttachments((prev) => {
        const imageToRemove = prev.find((img) => img.id === imageId);
        if (imageToRemove) {
          URL.revokeObjectURL(imageToRemove.preview);
        }
        return prev.filter((img) => img.id !== imageId);
      });
    }, []);

    const handleCloseModal = useCallback(() => {
      imageAttachments.forEach((img) => URL.revokeObjectURL(img.preview));
      setFormData(INITIAL_FORM_STATE);
      setSubmitError(null);
      setMarkdownErrors([]);
      setImageAttachments([]);
      setUploadProgress(0);
      onClose();
    }, [onClose, imageAttachments]);

    const handleSubmit = useCallback(async () => {
      if (!isFormValid || isSubmitting) return;

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const eventId = Sentry.captureMessage(
          `User Feedback [${formData.severity.toUpperCase()}]: ${
            formData.name
          } - ${moment().format("MMMM Do YYYY, h:mm:ss a")}`,
          formData.severity
        );
        Sentry.withScope((scope) => {
          scope.setUser({
            email: formData.email.trim(),
            username: formData.name.trim(),
          });

          scope.setTag("feedback.type", formData.severity);
          if (imageAttachments.length > 0) {
            scope.setTag("has_attachments", "true");
            scope.setTag(
              "attachment_count",
              imageAttachments.length.toString()
            );
          }

          scope.setContext("user_feedback", {
            name: formData.name.trim(),
            email: formData.email.trim(),
            message: formData.feedback.trim(),
            timestamp: Date.now(),
          });

          scope.addEventProcessor((event, hint) => {
            if (imageAttachments.length > 0) {
              hint.attachments = imageAttachments.map((img) => {
                const base64Data = img.base64.split(",")[1];
                const binaryData = Uint8Array.from(atob(base64Data), (c) =>
                  c.charCodeAt(0)
                );

                return {
                  filename: img.file.name,
                  contentType: img.file.type,
                  data: binaryData,
                };
              });
            }
            return event;
          });

          const event: Sentry.Event = {
            event_id: eventId,

            message: formData.feedback,
            level: formData.severity,
            user: {
              email: formData.email.trim(),
              username: formData.name.trim(),
            },
            tags: {
              "feedback.type": formData.severity,
              "feedback.has_attachments":
                imageAttachments.length > 0 ? "true" : "false",
            },
            breadcrumbs: [
              {
                category: "user-feedback",
                message: "User submitted feedback",
                level: "info",
                data: {
                  feedbackType: formData.severity,
                  hasContent: formData.feedback.length > 0,
                  eventId: eventId,
                },
              },
            ],
            type: "feedback",
            timestamp: Date.now() / 1000,
            server_name: window.location.hostname,
          };
          Sentry.captureEvent(event);
        });

        Sentry.captureFeedback({
          associatedEventId: eventId,
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.feedback.trim(),
        });

        handleCloseModal();

        addToast({
          title: translate("anketa.feedback.submit.success.title", {
            defaultMessage: "Feedback Submitted",
          }),
          color: "success",
          text: translate("anketa.feedback.submit.success.message", {
            defaultMessage:
              "Thank you for your feedback! We appreciate your input and will review it shortly.",
          }),
        });
      } catch (error) {
        addToast({
          title: translate("anketa.feedback.submit.error.title", {
            defaultMessage: "Feedback Submission Failed",
          }),
          color: "danger",
          text: translate("anketa.feedback.submit.error.message", {
            defaultMessage:
              "We were unable to submit your feedback. Please check your connection and try again.",
          }),
        });

        console.error("Failed to submit feedback:", error);
        setSubmitError(
          translate("anketa.feedback.submit.error.message", {
            defaultMessage: "Failed to submit feedback. Please try again.",
          })
        );

        Sentry.captureException(error, {
          tags: {
            section: "user_feedback",
            action: "submit_failed",
          },
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [
      formData,
      isFormValid,
      isSubmitting,
      handleCloseModal,
      imageAttachments,
    ]);

    return (
      <EuiModal
        onClose={handleCloseModal}
        maxWidth="800px"
        aria-labelledby="feedback-modal-title"
      >
        <EuiModalHeader style={{ justifyContent: "flex-start" }}>
          <AutonubilSvg style={{ height: "50px", width: "50px" }} />

          <EuiModalHeaderTitle
            id="feedback-modal-title"
            style={{ marginLeft: "15px" }}
          >
            {translate("anketa.feedback.modal.title", {
              defaultMessage: "Share Your Feedback",
            })}
          </EuiModalHeaderTitle>
        </EuiModalHeader>

        <EuiModalBody>
          {submitError && (
            <EuiCallOut
              title={translate("anketa.feedback.submit.error.title", {
                defaultMessage: "Submission Failed",
              })}
              color="danger"
              iconType="alert"
              style={{ marginBottom: "16px" }}
            >
              <p>{submitError}</p>
            </EuiCallOut>
          )}

          <EuiForm>
            <EuiFormRow
              label={translate("anketa.feedback.form.name.label", {
                defaultMessage: "Your Name",
              })}
              isInvalid={!formData.name.trim() && formData.name.length > 0}
              error={
                !formData.name.trim() && formData.name.length > 0
                  ? [
                      translate("anketa.feedback.form.name.required", {
                        defaultMessage: "Name is required",
                      }),
                    ]
                  : []
              }
            >
              <EuiFieldText
                readOnly
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.name}
                onChange={(e) => handleFieldChange("name")(e.target.value)}
                aria-label={translate("anketa.feedback.form.name.label", {
                  defaultMessage: "Your Name",
                })}
                placeholder={translate(
                  "anketa.feedback.form.name.placeholder",
                  {
                    defaultMessage: "Enter your Name",
                  }
                )}
                disabled={isSubmitting}
              />
            </EuiFormRow>

            <EuiFormRow
              label={translate("anketa.feedback.form.email.label", {
                defaultMessage: "Your Name",
              })}
              isInvalid={!formData.name.trim() && formData.name.length > 0}
              error={
                !formData.name.trim() && formData.name.length > 0
                  ? [
                      translate("anketa.feedback.form.email.required", {
                        defaultMessage: "Name is required",
                      }),
                    ]
                  : []
              }
            >
              <EuiFieldText
                readOnly
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={(e) => handleFieldChange("email")(e.target.value)}
                aria-label={translate("anketa.feedback.form.email.label", {
                  defaultMessage: "Your Email",
                })}
                placeholder={translate(
                  "anketa.feedback.form.email.placeholder",
                  {
                    defaultMessage: "Enter your email address",
                  }
                )}
                disabled={isSubmitting}
              />
            </EuiFormRow>

            <EuiFormRow
              label={translate("anketa.feedback.form.type.label", {
                defaultMessage: "Feedback Type",
              })}
            >
              <EuiButtonGroup
                legend={translate("anketa.feedback.form.type.legend", {
                  defaultMessage: "Select feedback type",
                })}
                options={feedbackTypes}
                idSelected={formData.severity}
                onChange={handleSeverityChange}
                buttonSize="s"
                isDisabled={isSubmitting}
              />
            </EuiFormRow>

            <EuiFormRow
              label={translate("anketa.feedback.form.content.label", {
                defaultMessage: "Your Feedback",
              })}
              helpText={translate("anketa.feedback.form.content.help", {
                defaultMessage:
                  "Describe your feedback, bug report, or feature request in detail",
              })}
              isInvalid={markdownErrors.length > 0}
              error={markdownErrors.map(
                (err) =>
                  err.message ||
                  err.toString() ||
                  translate("anketa.feedback.markdown.error", {
                    defaultMessage: "Markdown parsing error",
                  })
              )}
            >
              <EuiMarkdownEditor
                aria-label={translate("anketa.feedback.form.content.aria", {
                  defaultMessage: "Feedback content",
                })}
                value={formData.feedback}
                onChange={handleFieldChange("feedback")}
                height={300}
                onParse={handleMarkdownParse}
                autoExpandPreview
                initialViewMode="editing"
                placeholder={translate(
                  "anketa.feedback.form.content.placeholder",
                  {
                    defaultMessage: "Please provide detailed feedback...",
                  }
                )}
              />
            </EuiFormRow>

            {development && (
              <EuiFormRow
                label={translate("anketa.feedback.form.images.label", {
                  defaultMessage: "Screenshots or Images (Optional)",
                })}
                helpText={translate("anketa.feedback.form.images.help", {
                  defaultMessage:
                    "Upload screenshots or images to help explain your feedback. Max 5 images, 5MB each.",
                })}
              >
                <div>
                  <EuiFilePicker
                    id="feedback-image-picker"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    display="large"
                    aria-label={translate("anketa.feedback.form.images.aria", {
                      defaultMessage: "Upload images",
                    })}
                    isLoading={uploadProgress > 0 && uploadProgress < 100}
                    disabled={
                      isSubmitting || imageAttachments.length >= MAX_IMAGES
                    }
                  />

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div style={{ marginTop: "8px" }}>
                      <EuiProgress value={uploadProgress} max={100} size="xs" />
                    </div>
                  )}

                  {imageAttachments.length > 0 && (
                    <div style={{ marginTop: "16px" }}>
                      <EuiText size="s" style={{ marginBottom: "8px" }}>
                        <strong>
                          {translate("anketa.feedback.form.images.attached", {
                            defaultMessage: "Attached Images ({count}/{max})",
                            values: {
                              count: imageAttachments.length,
                              max: MAX_IMAGES,
                            },
                          })}
                        </strong>
                      </EuiText>
                      <EuiFlexGroup gutterSize="s" wrap>
                        {imageAttachments.map((image) => (
                          <EuiFlexItem key={image.id} grow={false}>
                            <EuiPanel
                              paddingSize="s"
                              style={{
                                position: "relative",
                                maxWidth: "120px",
                              }}
                            >
                              <EuiImage
                                src={image.preview}
                                alt={image.file.name}
                                style={{
                                  maxHeight: "80px",
                                  objectFit: "cover",
                                }}
                              />
                              <EuiButtonIcon
                                iconType="cross"
                                color="danger"
                                size="s"
                                style={{
                                  position: "absolute",
                                  top: "4px",
                                  right: "4px",
                                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                                }}
                                onClick={() => handleRemoveImage(image.id)}
                                aria-label={translate(
                                  "anketa.feedback.form.images.remove",
                                  {
                                    defaultMessage: "Remove image",
                                  }
                                )}
                                disabled={isSubmitting}
                              />
                              <EuiText
                                size="xs"
                                style={{
                                  marginTop: "4px",
                                  textAlign: "center",
                                }}
                              >
                                {image.file.name.length > 15
                                  ? `${image.file.name.substring(0, 12)}...`
                                  : image.file.name}
                              </EuiText>
                            </EuiPanel>
                          </EuiFlexItem>
                        ))}
                      </EuiFlexGroup>
                    </div>
                  )}
                </div>
              </EuiFormRow>
            )}
          </EuiForm>
        </EuiModalBody>

        <EuiModalFooter>
          <EuiButtonEmpty onClick={handleCloseModal} disabled={isSubmitting}>
            {translate("anketa.feedback.submit.cancel", {
              defaultMessage: "Cancel",
            })}
          </EuiButtonEmpty>

          <EuiButton
            type="submit"
            onClick={handleSubmit}
            fill
            isDisabled={!isFormValid || isSubmitting}
            isLoading={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <EuiLoadingSpinner size="s" />
                <span style={{ marginLeft: "8px" }}>
                  {translate("anketa.feedback.submit.submitting", {
                    defaultMessage: "Submitting...",
                  })}
                </span>
              </>
            ) : (
              translate("anketa.feedback.submit.button", {
                defaultMessage: "Submit Feedback",
              })
            )}
          </EuiButton>
        </EuiModalFooter>
      </EuiModal>
    );
  }
);

OpsUserFeedbackModal.displayName = "OpsUserFeedbackModal";
